// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { UserWebStorageInstance } from '../../user-web-storage';
import { YoutubeExtLink } from './ExternalLink/YoutubeExtLink';
import { WebContainerExtLink } from './ExternalLink/WebContainerExtLink';
import { AttachingArea } from './ExternalLink/BaseExtLink';
import { AutochartistExtLink } from './ExternalLink/AutochartistExtLink';
import { LinkTokenLangExtLink } from './ExternalLink/LinkTokenLangExtLink';
import { NoneExtLink } from './ExternalLink/NoneExtLink';
import { AutochartistIFrameExtLink } from './ExternalLink/AutochartistIFrameExtLink';
import { TradingCentralExtLink } from './ExternalLink/TradingCentralExtLink';
import { DataCache } from '../DataCache';
import { ExternalLinkType } from './ExternalLink/Branding/ExternalLinkType';
import { ITChartAdvancedExtLink } from './ExternalLink/ITChartAdvancedExtLink';
import { TokenPostExtLink } from './ExternalLink/TokenPostExtLink';
export class ExternalLinksCache {
    constructor() {
        this.linkTools = new Map();
        this.onAdd = new CustomEvent();
        this.onUpdate = new CustomEvent();
        this.onRemove = new CustomEvent();
        this.onYtDataUpdate = new CustomEvent();
        this.ytTimer = null;
        this.ytUpdateInterval = 10 * 60 * 1000; // 10 minutes
        this.ytRequestCount = 0;
        this.ytFirstRequest = true;
    }
    processExternalLinkMessage(externalLinkMessage) {
        const linkID = externalLinkMessage.Id;
        if (!linkID)
            return;
        let brandingToolParams = null;
        if (externalLinkMessage.ExternalLinkParameters) {
            try {
                const parsedParams = JSON.parse(externalLinkMessage.ExternalLinkParameters);
                if (!isNullOrUndefined(parsedParams.attachingArea)) {
                    parsedParams.attachingArea = Number(parsedParams.attachingArea);
                    if (isNaN(parsedParams.attachingArea)) {
                        parsedParams.attachingArea = AttachingArea.PopUp;
                    }
                }
                brandingToolParams = parsedParams;
            }
            catch (e) { }
        }
        let isNewLink = false;
        let linkTool = this.linkTools.get(linkID);
        if (!isNullOrUndefined(externalLinkMessage.ExternalResource)) {
            if (!linkTool || linkTool.brandingToolExtResource !== externalLinkMessage.ExternalResource) {
                linkTool = this.getObjectByExtResource(externalLinkMessage.ExternalResource);
                this.linkTools.set(linkID, linkTool);
                isNewLink = true;
            }
            linkTool === null || linkTool === void 0 ? void 0 : linkTool.update(brandingToolParams, externalLinkMessage);
        }
        if (DataCache.Loaded) {
            if (isNewLink) {
                this.onAdd.Raise(linkTool);
            }
            else {
                this.onUpdate.Raise(linkTool);
            }
            if (linkTool instanceof YoutubeExtLink) {
                void this.ytRequestLinks([linkTool]);
            }
        }
    }
    processExternalLinkRemoveMessage(externalLinkRemove) {
        const linkID = externalLinkRemove.Id;
        if (!linkID)
            return;
        const deleteLink = this.linkTools.get(linkID);
        this.linkTools.delete(linkID);
        this.onRemove.Raise(deleteLink);
    }
    clear() {
        this.linkTools.clear();
        this.ytRequestCount = 0;
        this.ytFirstRequest = true;
        if (!isNullOrUndefined(this.ytTimer)) {
            clearInterval(this.ytTimer);
            this.ytTimer = null;
        }
    }
    getLinkById(id) {
        if (id === null) {
            return null;
        }
        return this.linkTools[id];
    }
    getExtLinks() {
        return Array.from(this.linkTools.values());
    }
    getWebContainerExtLinks() {
        return this.getToolsOfType(WebContainerExtLink);
    }
    getAutochartistIFrameExtLinks() {
        return this.getToolsOfType(AutochartistIFrameExtLink);
    }
    getNoneExtLinks() {
        return this.getToolsOfType(NoneExtLink);
    }
    getLinkTokenLangExtLinks() {
        return this.getToolsOfType(LinkTokenLangExtLink);
    }
    getAutochartistExtLink() {
        return this.getExtLinks().find((linkTool) => linkTool instanceof AutochartistExtLink);
    }
    getTradingCentralExtLink() {
        return this.getExtLinks().find((linkTool) => linkTool instanceof TradingCentralExtLink);
    }
    getITChartAdvancedExtLink() {
        return this.getExtLinks().find((linkTool) => linkTool instanceof ITChartAdvancedExtLink);
    }
    getYoutubeExtLinks() {
        return this.getExtLinks()
            .filter((linkTool) => linkTool instanceof YoutubeExtLink)
            .filter((linkTool) => new Date() <= linkTool.availUntilDate);
    }
    getObjectByExtResource(extResource) {
        var _a;
        switch (extResource) {
            case ExternalLinkType.AUTOCHARTIST_RP:
                return new AutochartistExtLink();
            case ExternalLinkType.NONE:
                return new NoneExtLink();
            case ExternalLinkType.LINK_TOKEN_LANG:
                return new LinkTokenLangExtLink();
            case ExternalLinkType.TRADING_CENTRAL:
                return new TradingCentralExtLink();
            case ExternalLinkType.AUTOCHARTIST_IFRAME:
                return new AutochartistIFrameExtLink();
            case ExternalLinkType.ITCHART_ADVANCED:
                return new ITChartAdvancedExtLink();
            case ExternalLinkType.YOUTUBE_LINK:
                return new YoutubeExtLink();
            case ExternalLinkType.WEB_CONTAINER:
                return new WebContainerExtLink();
            case ExternalLinkType.TOKEN_POST:
                return new TokenPostExtLink();
            default:
                const extResourceName = (_a = ExternalLinkType[extResource]) !== null && _a !== void 0 ? _a : `Unknown (${extResource})`;
                console.warn(`External resource type "${extResourceName}" is currently unsupported on Web and Web Mobile platforms.`);
                return new NoneExtLink();
        }
    }
    ytStartScheduler() {
        return __awaiter(this, void 0, void 0, function* () {
            const linkTools = this.ytOptimizedLinks();
            yield this.ytRequestLinks(linkTools);
            this.ytTimer = setInterval(() => {
                const linkTools = this.ytOptimizedLinks();
                this.ytRequestLinks(linkTools).catch((error) => {
                    console.error('Error in ytRequestLinks:', error);
                });
            }, this.ytUpdateInterval);
        });
    }
    ytOptimizedLinks() {
        const linkTools = this.getYoutubeExtLinks();
        const filteredTools = linkTools.filter((tool) => {
            var _a, _b;
            if (!tool.getUrl()) {
                return false;
            }
            const videoCreateDate = tool.config.getVideoCreateDate();
            const streamStartDate = tool.config.getStreamStartDate();
            const streamEndDate = tool.config.getStreamEndDate();
            const wasCreatedToday = (_a = videoCreateDate === null || videoCreateDate === void 0 ? void 0 : videoCreateDate.isSameDay(new Date())) !== null && _a !== void 0 ? _a : false;
            const wasStreamStartedToday = (_b = streamStartDate === null || streamStartDate === void 0 ? void 0 : streamStartDate.isSameDay(new Date())) !== null && _b !== void 0 ? _b : false;
            if (this.ytFirstRequest || wasCreatedToday || (wasStreamStartedToday && streamEndDate === null)) {
                return true;
            }
            return false;
        });
        return filteredTools;
    }
    ytRequestLinks(linkTools) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ytRequestCount = linkTools.length;
            for (const linkTool of linkTools) {
                const linkID = linkTool.id;
                try {
                    const resp = yield UserWebStorageInstance.youtubePageParser.post(linkTool.getUrl(), linkTool.videoID);
                    const tool = this.linkTools.get(linkID);
                    if (!isNullOrUndefined(resp === null || resp === void 0 ? void 0 : resp.data))
                        tool === null || tool === void 0 ? void 0 : tool.config.update(resp.data);
                }
                catch (error) {
                    console.error(`Failed to fetch or process URL for linkID ${linkID}:`, error);
                }
                finally {
                    this.ytRequestCount -= 1;
                    this.onYtDataUpdate.Raise(linkTool);
                    if (this.ytRequestCount === 0) {
                        this.ytFirstRequest = false;
                    }
                }
            }
        });
    }
    getToolsOfType(toolClass) {
        return this.getExtLinks()
            .filter((linkTool) => linkTool instanceof toolClass)
            .sort((tool1, tool2) => tool1.id - tool2.id);
    }
}
